import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Input,
  CircularProgress,
  Button,
  Sheet,
  Select,
  Option
} from '@mui/joy';
import { ChevronLeft } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function DettaglioMezzo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [conventions, setConventions] = useState([]);

  const [saving, setSaving] = useState(false);

  const [initialName, setInitialName] = useState('');
  const [initialCategoryId, setInitialCategoryId] = useState(null);
  const [initialConventionId, setInitialConventionId] = useState(null);

  const selectedOrg = localStorage.getItem('selectedOrg') || '1';

  // Fetch risorsa
  useEffect(() => {
    const fetchVehicle = async () => {
      setLoading(true);
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/vehicle/${id}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error('Errore fetch risorsa');
        const data = await res.json();

        setVehicle(data);
        setInitialName(data.name_vehicle || '');
        setInitialCategoryId(data.category_id ?? null);
        setInitialConventionId(data.convention_id ?? null);
      } catch (err) {
        console.error('Errore nel fetch risorsa:', err);
        toast.error('Errore nel caricamento del risorsa', { position: 'top-right' });
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [selectedOrg, id]);

  // Fetch categories + conventions
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const [resCategories, resConventions] = await Promise.all([
          authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/category`, {
            credentials: 'include',
          }),
          authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/convention`, {
            credentials: 'include',
          }),
        ]);

        if (!resCategories.ok) throw new Error('Errore fetch categorie');
        if (!resConventions.ok) throw new Error('Errore fetch convenzioni');

        const categoriesData = await resCategories.json();
        const conventionsData = await resConventions.json();

        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setConventions(Array.isArray(conventionsData) ? conventionsData : []);
      } catch (err) {
        console.error('Errore nel fetch liste:', err);
        toast.error('Errore nel caricamento dati', { position: 'top-right' });
        setCategories([]);
        setConventions([]);
      }
    };

    fetchLists();
  }, [selectedOrg]);

  const handleToggleStatus = async () => {
    if (!vehicle) return;
    const nuovoStatus = vehicle.status === 1 ? 0 : 1;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/vehicle/${id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: nuovoStatus,
            vehicle_id: parseInt(id, 10),
          }),
        }
      );
      if (!res.ok) throw new Error('Errore nella modifica dello stato risorsa');

      setVehicle((prev) => ({ ...prev, status: nuovoStatus }));
      toast.success(
        nuovoStatus === 1 ? 'Risorsa attivato con successo' : 'Risorsa disattivato con successo',
        { position: 'top-right', autoClose: 3000 }
      );
    } catch (err) {
      console.error('Errore toggle stato risorsa:', err);
      toast.error('Errore nel cambio di stato del risorsa', { position: 'top-right', autoClose: 3000 });
    }
  };

  const handleSalva = async () => {
    if (!vehicle) return;
    setSaving(true);

    const body = {
      vehicle_id: parseInt(id, 10),
      name: vehicle.name_vehicle,
      category_id: vehicle.category_id !== initialCategoryId ? vehicle.category_id : null,
      convention_id: (vehicle.convention_id ?? null) !== (initialConventionId ?? null)
        ? (vehicle.convention_id ?? null)
        : null,
    };

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/vehicle/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) throw new Error('Errore nel salvataggio risorsa');

      toast.success('Modifiche salvate con successo', { position: 'top-right', autoClose: 3000 });
      setInitialName(vehicle.name_vehicle);
      setInitialCategoryId(vehicle.category_id ?? null);
      setInitialConventionId(vehicle.convention_id ?? null);
    } catch (err) {
      console.error('Errore salvataggio risorsa:', err);
      toast.error('Errore nel salvataggio', { position: 'top-right', autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return isNaN(date) ? '' : date.toLocaleDateString('it-IT');
  };

  const isModified =
    vehicle &&
    (
      vehicle.name_vehicle !== initialName ||
      (vehicle.category_id ?? null) !== (initialCategoryId ?? null) ||
      (vehicle.convention_id ?? null) !== (initialConventionId ?? null)
    );

  return (
    <Box>
      <Typography level="h3" mb={1}>
        Dettaglio Risorsa
      </Typography>

      <Button
        startDecorator={<ChevronLeft />}
        onClick={() => navigate(-1)}
        variant="outlined"
        size="sm"
        sx={{ mb: 3 }}
      >
        Indietro
      </Button>

      {loading ? (
        <CircularProgress />
      ) : !vehicle ? (
        <Typography color="danger">Risorsa non trovata.</Typography>
      ) : (
        <Sheet
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 'md',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 280,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: vehicle.status === 1 ? 'success.500' : 'danger.500',
              }}
            />
            <Typography
              level="body-md"
              sx={{ color: vehicle.status === 1 ? 'success.600' : 'danger.600' }}
            >
              {vehicle.status === 1 ? 'Attivo' : 'Disattivo'}
            </Typography>
          </Box>

          <Input
            value={vehicle.name_vehicle || ''}
            placeholder="Nome risorsa"
            startDecorator={<Typography level="body-sm">Nome</Typography>}
            onChange={(e) =>
              setVehicle((prev) => ({ ...prev, name_vehicle: e.target.value }))
            }
          />

          <Input
            disabled
            value={vehicle.code_vehicle || ''}
            placeholder="Codice"
            startDecorator={<Typography level="body-sm">Codice</Typography>}
          />

          {/* CATEGORY con label */}
          <Select
            value={vehicle.category_id ?? ''}
            onChange={(event, newValue) =>
              setVehicle((prev) => ({ ...prev, category_id: newValue }))
            }
            placeholder="Seleziona categoria"
            startDecorator={<Typography level="body-sm">Categoria</Typography>}
          >
            {categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>

          {/* CONVENTION dropdown */}
          <Select
            value={vehicle.convention_id ?? ''}
            onChange={(event, newValue) =>
              setVehicle((prev) => ({ ...prev, convention_id: newValue }))
            }
            placeholder="Seleziona convenzione"
            startDecorator={<Typography level="body-sm">Convenzione</Typography>}
          >
            {/* opzionale: permette di "svuotare" */}
            <Option value="">Nessuna</Option>

            {conventions.map((conv) => (
              <Option key={conv.id} value={conv.id}>
                {conv.name}
              </Option>
            ))}
          </Select>

          <Input
            disabled
            value={formatDate(vehicle.last_check)}
            placeholder="Ultimo controllo"
            startDecorator={<Typography level="body-sm">Ultimo check</Typography>}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              size="sm"
              onClick={handleSalva}
              loading={saving}
              disabled={saving || !isModified}
              color="primary"
            >
              Salva
            </Button>

            <Button
              size="sm"
              variant="solid"
              color={vehicle.status === 1 ? 'danger' : 'success'}
              onClick={handleToggleStatus}
              disabled={saving}
            >
              {vehicle.status === 1 ? 'Disattiva' : 'Attiva'}
            </Button>
          </Box>
        </Sheet>
      )}

      <ToastContainer />
    </Box>
  );
}